import React, { useEffect, useState } from "react";

import { Add, Delete, KeyboardArrowUp } from "@material-ui/icons";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { Button, FormControl, FormLabel, Typography } from "@portex-pro/ui-components";
import { ShipperAddress } from "api/rest/address/types";
import NotesIcon from "assets/quote-request/notes.svg";
import LocationPicker from "components/addresses/LocationPicker";
import DoubleTimeRangeSelector from "components/DoubleTimeRangeSelector";
import { Guide } from "components/Guide";
import SingleCalendarPicker from "components/SingleCalendarPicker";
import SingleTimeRangeSelector from "components/SingleTimeRangeSelector";
import { DateTime } from "luxon";
import { Controller, useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { TimeRange } from "types/TimeRange";
import { insertToArray } from "utils/insertToArray";
import { removeFromArray } from "utils/removeFromArray";
import { replaceInArray } from "utils/replaceInArray";

import MemoizedTextInput from "../../_components/MemoizedTextInput";
import StepCollapse from "../../_components/StepCollapse";
import { useCreateStepCollapseProps } from "../../_hooks/useStepCollapse";
import { useQuoteRequestSelector } from "../../_store";
import { quoteRequestActions } from "../../_store/quoteRequestSliceV2";
import { QuoteStepIndex } from "../../types";
import { FinalSchema, StopSchema } from "../_schemas";
import { formatLocationShort } from "../_utils/descriptorFormatters";

type AddressLike = {
  name?: string | null;
  city?: string | null;
  province_name?: string | null;
  country_code?: string | null;
};

type StopLike = {
  address?: AddressLike | null;
};

const LocationStepTag = ({ stops }: { stops?: StopLike[] }) => {
  const { t } = useTranslation("quoteRequest");

  if (!stops || stops.length === 0) {
    return <> </>;
  }

  const firstStop = stops[0]?.address;
  const lastStop = stops[stops.length - 1]?.address;

  if (!firstStop && !lastStop) {
    return <></>;
  }

  const pickupLocation = formatLocationShort(firstStop as ShipperAddress);
  const deliveryLocation = formatLocationShort(lastStop as ShipperAddress);

  return (
    <div className="inline-flex items-center bg-blue-50 rounded-md px-2 py-1">
      <span className="text-blue-600 font-bold mr-2 text-sm">
        {t("LocationStep.pickupToDelivery", {
          pickup: pickupLocation || t("LocationStep.pickup"),
          delivery: deliveryLocation || t("LocationStep.delivery"),
        })}
      </span>
    </div>
  );
};

export const LocationStep: React.VFC<{ onSubmit: (data: FinalSchema) => void }> = ({ onSubmit }) => {
  const { t } = useTranslation(["common", "shipper", "quoteRequest"]);
  const setQuoteRequestAction = quoteRequestActions.useSetQuoteRequest();
  const setQuoteRequestByPath = quoteRequestActions.useSetQuoteRequestByPath();
  const setStepIndex = quoteRequestActions.useSetStepIndex();
  const { stepIndex, quoteRequest: airQuoteRequest } = useQuoteRequestSelector((store) => store.quoteRequestSliceV2);
  const { baseStepCollapseProps, setIsOpen } = useCreateStepCollapseProps({
    expectedStepIndex: QuoteStepIndex.Location,
    name: t("shipper:location"),
    number: 1,
  });

  const { field } = useController<FinalSchema>({ name: "stops" });
  const methods = useFormContext<FinalSchema>();

  const stops = useQuoteRequestSelector((store) => store.quoteRequestSliceV2.quoteRequest.stops);
  const [useTimeRange, setUseTimeRange] = useState([false, false]);
  const [tabValues, setTabValues] = useState<Array<boolean>>([false, false]);

  const changeStopAddress = (address: StopSchema["address"], index: number) => {
    setQuoteRequestByPath({ path: `stops.${index}.address`, value: address });
  };

  useEffect(() => {
    if (airQuoteRequest.id === undefined && !baseStepCollapseProps.collapseIn) {
      setIsOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [airQuoteRequest]);

  const handleDateChange = (value: DateTime | null, index: number) => {
    const stop = stops?.[index];
    if (!value) {
      setQuoteRequestByPath({
        path: `stops.${index}`,
        value: { ...stops?.[index], start: null, end: null, is_time_tbd: false },
      });
      return;
    }
    let start = value
      ?.startOf("day")
      .setZone(stop?.address?.iana_timezone ?? DateTime.local().zone, { keepLocalTime: true });
    let end = value
      ?.endOf("day")
      .setZone(stop?.address?.iana_timezone ?? DateTime.local().zone, { keepLocalTime: true });

    if (start && stop?.start && stop.address?.iana_timezone) {
      start = start
        .setZone(stop.address.iana_timezone, { keepLocalTime: true })
        .set({ hour: DateTime.fromISO(stop.start).hour, minute: DateTime.fromISO(stop.start).minute });
    }
    if (end && stop?.end && stop.address?.iana_timezone) {
      end = end
        .setZone(stop.address.iana_timezone, { keepLocalTime: true })
        .set({ hour: DateTime.fromISO(stop.end).hour, minute: DateTime.fromISO(stop.end).minute });
    }
    setQuoteRequestByPath({
      path: `stops.${index}`,
      value: {
        ...stops?.[index],
        start: start.toISO(),
        end: end.toISO(),
        is_time_tbd: stops?.[0]?.is_time_tbd !== undefined ? undefined : true,
      },
    });
  };

  const handleTimeRangeChange = (range: TimeRange | null, index: number) => {
    const stop = stops?.[index];
    if (!range) {
      setQuoteRequestByPath({
        path: `stops.${index}`,
        value: { ...stops?.[index], start: null, end: null, is_time_tbd: false },
      });
      return;
    }
    let start: MaterialUiPickersDate | TimeRange | undefined = range.start;
    let end: MaterialUiPickersDate | TimeRange | undefined = range.end;

    if (stop?.address?.iana_timezone) {
      start = start?.setZone(stop?.address?.iana_timezone, { keepLocalTime: true });
      end = end?.setZone(stop?.address?.iana_timezone, { keepLocalTime: true });
    }
    setQuoteRequestByPath({
      path: `stops.${index}`,
      value: { ...stops?.[index], start: start?.toISO(), end: end?.toISO(), is_time_tbd: range.isTimeTBD },
    });
  };

  const submit = async (data: FinalSchema) => {
    await onSubmit(data);
    setStepIndex(QuoteStepIndex.LoadAttributes);
    setIsOpen(false);
  };

  return (
    <StepCollapse
      {...baseStepCollapseProps}
      tag={<LocationStepTag stops={stops} />}
      footer={
        stepIndex <= QuoteStepIndex.Location ? (
          <div className="flex justify-between mt-2 w-full">
            <div>
              {methods.formState.errors.stops?.message && (
                <Typography variant="caption" color="error">
                  {methods.formState.errors.stops.message}
                </Typography>
              )}
            </div>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={methods.handleSubmit(submit)}
              style={{ width: 150 }}
            >
              {t("common:next")}
            </Button>
          </div>
        ) : null
      }
    >
      <div className="flex flex-col w-full">
        {stops?.map((stop, index) => (
          <>
            {index > 0 && (
              <div
                className="w-full flex items-center justify-center mt-2 border-grey-300 text-grey-300 group-hover:border-blue-500 hover:text-blue-500 hover:cursor-pointer group"
                onClick={() => {
                  setQuoteRequestAction({
                    // @ts-expect-error this is fine for now
                    stops: insertToArray(field.value as StopSchema[], index, { address: {} }),
                  });
                  setUseTimeRange(insertToArray(useTimeRange, index, false));
                }}
              >
                <div className="border w-full group-hover:border-blue-500" />
                <div className="w-6 h-6 border-2 rounded-full flex items-center justify-center p-4 group-hover:border-blue-500">
                  <Add />
                </div>
                <div className="border w-full group-hover:border-blue-500" />
              </div>
            )}
            <div className="flex flex-col w-full mt-6">
              <div className="flex w-full space-x-3">
                <Controller
                  control={methods.control}
                  name={`stops.${index}.address`}
                  render={({ field, fieldState }) => (
                    <Guide className="w-1/2" showGuide={!field.value && !fieldState.error}>
                      <LocationPicker
                        label={
                          index === 0
                            ? t("common:pickup")
                            : index === (stops?.length ?? 0) - 1
                            ? t("common:delivery")
                            : t("common:stopLabel", { no: index })
                        }
                        // @ts-expect-error this type issue is fine for now
                        value={field.value}
                        mode="FTL"
                        onChange={(stopAddress) => changeStopAddress(stopAddress as StopSchema["address"], index)}
                        required={true}
                        highlight={Boolean(fieldState.error)}
                        startIconPallete="grey"
                      />
                      {fieldState.error && (
                        <span className="text-xs font-bold leading-relaxed tracking-widest text-red-600">
                          {fieldState.error.message}
                        </span>
                      )}
                    </Guide>
                  )}
                />

                <div className="w-1/4">
                  <Controller
                    control={methods.control}
                    name={`stops.${index}.start`}
                    render={({ field: _field, fieldState }) => (
                      <>
                        <FormControl fullWidth>
                          <FormLabel>{t("shipper:date")}</FormLabel>
                          <SingleCalendarPicker
                            value={stop.start ? DateTime.fromISO(stop.start, { setZone: true }) : null}
                            onChange={(value) => handleDateChange(value, index)}
                            disabled={!stops?.[index].address}
                            startIconPallete="grey"
                          />
                        </FormControl>
                        {fieldState.error && (
                          <span className="text-xs font-bold leading-relaxed tracking-widest text-red-600">
                            {fieldState.error.message}
                          </span>
                        )}
                      </>
                    )}
                  />
                </div>

                <div className="w-1/4">
                  <Controller
                    control={methods.control}
                    name={`stops.${index}`}
                    render={({ field, fieldState }) => {
                      return (
                        <>
                          <FormControl fullWidth>
                            <FormLabel>{t("shipper:time")}</FormLabel>
                            {useTimeRange[index] ? (
                              <DoubleTimeRangeSelector
                                onChange={(value) => handleTimeRangeChange(value, index)}
                                value={{
                                  start: field.value.start
                                    ? DateTime.fromISO(field.value.start, {
                                        // @ts-expect-error the iana timezone will exist
                                        zone: field.value.address?.iana_timezone ?? DateTime.local().zone,
                                      })
                                    : null,
                                  end: field.value.end
                                    ? DateTime.fromISO(field.value.end, {
                                        // @ts-expect-error the iana timezone will exist
                                        zone: field.value.address?.iana_timezone ?? DateTime.local().zone,
                                      })
                                    : null,
                                  // @ts-expect-error we use is_time_tbd to match the update api
                                  isTimeTBD: field.value.is_time_tbd,
                                }}
                                style={{ flex: 1 }}
                                disabled={!stops?.[index].start}
                              />
                            ) : (
                              <SingleTimeRangeSelector
                                onChange={(value) => handleTimeRangeChange(value, index)}
                                disabled={!stops?.[index].start}
                                key={index}
                                value={{
                                  start: field.value.start
                                    ? DateTime.fromISO(field.value.start, {
                                        // @ts-expect-error the iana timezone will exist
                                        zone: field.value.address?.iana_timezone ?? DateTime.local().zone,
                                      })
                                    : null,
                                  end: field.value.end
                                    ? DateTime.fromISO(field.value.end, {
                                        // @ts-expect-error the iana timezone will exist
                                        zone: field.value.address?.iana_timezone ?? DateTime.local().zone,
                                      })
                                    : null,
                                  // @ts-expect-error we use is_time_tbd to match the update api
                                  isTimeTBD: field.value.is_time_tbd,
                                }}
                                style={{ flex: 1 }}
                                startIconPallete="grey"
                              />
                            )}
                          </FormControl>
                          {fieldState.error && (
                            <span className="text-xs font-bold leading-relaxed tracking-widest text-red-600">
                              {fieldState.error.message}
                            </span>
                          )}
                        </>
                      );
                    }}
                  />
                </div>
              </div>

              <div className="w-full flex justify-between items-center">
                <div className="flex items-center">
                  <Button
                    startIcon={
                      <span style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <img
                          src={NotesIcon}
                          alt="Add Notes"
                          style={{ width: 24, height: 24, display: "inline-block" }}
                        />
                      </span>
                    }
                    onClick={() =>
                      tabValues[index]
                        ? setTabValues(replaceInArray(tabValues, index, false))
                        : setTabValues(replaceInArray(tabValues, index, true))
                    }
                  >
                    <Typography variant="subtitle2" color="primary" style={{ fontWeight: 700 }}>
                      {t("quoteRequest:LocationStep.addNotes")}
                    </Typography>
                  </Button>
                </div>
                <div className="z-10">
                  {useTimeRange[index] ? (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      style={{ marginLeft: "auto", cursor: "pointer", textDecoration: "underline" }}
                      onClick={() => setUseTimeRange(replaceInArray(useTimeRange, index, false))}
                    >
                      {t("quoteRequest:LocationStep.useTime")}
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      style={{ marginLeft: "auto", cursor: "pointer", textDecoration: "underline" }}
                      onClick={() => setUseTimeRange(replaceInArray(useTimeRange, index, true))}
                    >
                      {t("quoteRequest:LocationStep.useTimeRange")}
                    </Typography>
                  )}
                </div>
              </div>

              {tabValues[index] && (
                <div className="flex w-full">
                  <div className="w-1/2">
                    <Controller
                      control={methods.control}
                      name={`stops.${index}.note`}
                      render={({ field, fieldState }) => (
                        <>
                          <MemoizedTextInput
                            key={`stop_${index}_note`}
                            value={field.value}
                            onChange={(e) =>
                              setQuoteRequestByPath({ path: `stops.${index}.note`, value: e.target.value })
                            }
                            margin="normal"
                            fullWidth
                            multiline
                            rows={3}
                            placeholder={t("quoteRequest:LocationStep.notesPlaceholder")}
                          />
                          {fieldState.error && (
                            <span className="text-xs font-bold leading-relaxed tracking-widest text-red-600">
                              {fieldState.error.message}
                            </span>
                          )}
                        </>
                      )}
                    />
                  </div>
                </div>
              )}
            </div>
            {
              <div className="flex flex-col w-full items-center -mt-5">
                <div
                  className="flex flex-col items-center border-grey-300 text-grey-300 group-hover:border-blue-500 hover:text-blue-500 hover:cursor-pointer group"
                  onClick={() => {
                    setQuoteRequestAction({
                      // @ts-expect-error this is fine for now
                      stops: removeFromArray(field.value as StopSchema[], index),
                    });
                    setUseTimeRange(removeFromArray(useTimeRange, index));
                  }}
                >
                  <KeyboardArrowUp className="opacity-0 group-hover:opacity-100" />
                  <Delete style={{ width: "18px", height: "18px" }} />
                </div>
              </div>
            }
          </>
        ))}
        {(stops?.length ?? 0) < 2 && (
          <div
            className="w-full flex items-center justify-center mt-2 border-grey-300 text-grey-300 group-hover:border-blue-500 hover:text-blue-500 hover:cursor-pointer group"
            onClick={() => {
              setQuoteRequestAction({
                // @ts-expect-error this is fine for now
                stops: insertToArray(field.value as StopSchema[], stops?.length ?? 0, { address: {} }),
              });
              setUseTimeRange(insertToArray(useTimeRange, stops?.length ?? 0, false));
            }}
          >
            <div className="border w-full group-hover:border-blue-500" />
            <div className="w-6 h-6 border-2 rounded-full flex items-center justify-center p-4 group-hover:border-blue-500">
              <Add />
            </div>
            <div className="border w-full group-hover:border-blue-500" />
          </div>
        )}
      </div>
    </StepCollapse>
  );
};
